/**
 * Utils for handling authentication with external providers
 * and managing SSO migration flags.
 */
import Utils from '@/globals/utils.js';
import CONST from '@/globals/constants.js';
import { fetchAuthSession as AmplifyFetchAuthSession } from 'aws-amplify/auth';
import { Dependencies } from '@/globals/utils.js'

const NEW_USER_THRESHOLD_SECONDS = 300; // 5 minutes

export const ssoGetMigrateAccountFlagFromUrl = (query) => {
  let res = null;

  if (query.sso_migrate_account) {
    res = Utils.decodeBase64Format(query.sso_migrate_account);
  }

  return res;
}

export const ssoGetCodeResponseFromUrl = (query) => {
  const res = {
    error: null,
    errorDescription: null,
    identity_provider: '',
    ssoPreSignUpFailed: false,
    ssoPreSignUpFailedEmail: null,
    ssoPreSignUpFailedProvider: null,
    sso: false,
  };

  if (query.error) {
    res.error = query.error;
  }

  if (query.error_description) {
    res.errorDescription = query.error_description;
  }

  if (res.errorDescription && (res.errorDescription.includes('PreSignUp failed') && res.errorDescription.includes('LinkingSSOUserError'))) {
    res.ssoPreSignUpFailed = true;
    const findEmail = query.error_description.match(/(?:[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?/g);
    res.ssoPreSignUpFailedEmail = findEmail.length ? findEmail[0] : null;
  }

  return res;
};

/**
 * Validates the structure of a Cognito identity object.
 *
 * @param {Object} identity - The identity object to validate.
 * @param {string|number|Object|null} [identity.<key>] - Each property of the identity object must be a string, number, object, or null.
 * @returns {boolean} - Returns `true` if the identity is valid, otherwise `false`.
 */
function isCognitoIdentityValid (identity) {
  return (
    typeof identity === 'object' &&
    identity !== null &&
    Object.values(identity).every(
      (value) => typeof value === 'string' || typeof value === 'number' || typeof value === 'object' || value === null
    )
  );
}

/**
 * Determines if a user identity was newly created based on the creation timestamp.
 *
 * @param {Array<Object>} identities - An array of identity objects.
 * Each object should have a `primary` property (indicating if it is the primary identity)
 * and a `dateCreated` property (a timestamp in milliseconds since the epoch).
 *
 * @param {number} [now=Date.now()] - The current timestamp in milliseconds.
 * Defaults to the current time.
 *
 * @returns {boolean} - Returns `true` if the primary identity's creation time is within the threshold (`NEW_USER_THRESHOLD_SECONDS`), otherwise `false`.
 *
 * @example
 * const identities = [
 *   { primary: 'false', dateCreated: 1690000000000 },
 *   { primary: 'true', dateCreated: 1690000300000 }
 * ];
 * const isNew = isUserIdentityNewlyCreated(identities);
 * console.log(isNew); // true if the current time is within 5 minutes of the primary identity's creation time
 */
export function isUserIdentityNewlyCreated (primaryIdentity, now = Date.now()) {
  // Check if user is newly created, (date created === date now + some threshold)
  const diffInSeconds = Math.abs((now - primaryIdentity.dateCreated) / 1000);
  return diffInSeconds <= NEW_USER_THRESHOLD_SECONDS;
}

export const handleProductEnableForExternalSignIn = async (idTokenPayload) => {
  if (!idTokenPayload) {
    console.warn('Session idToken.payload is null or undefined');
    return { success: false, error: 'label.loginError' };
  }

  const product = Utils.isPageForLicenses() ? CONST.PRODUCT_ACTIVATE : CONST.PRODUCT_ARYA;
  const preferredUsername = idTokenPayload.preferred_username;
  const identities = Array.isArray(idTokenPayload?.identities)
    ? idTokenPayload.identities.filter(isCognitoIdentityValid)
    : [];

  if (identities.length < 1) return { success: false };

  const primaryIdentity = identities.find(identity => identity?.primary === 'true' && ['Google', 'Facebook'].includes(identity?.providerName));

  if (!primaryIdentity) return { success: false };

  if (isUserIdentityNewlyCreated(primaryIdentity)) {
    try {
      const $api = Dependencies.inject('$api');
      await $api.patch(`${CONFIG.API_BASE_URL}/group/products`, { add: [product] })
      await $api.patch(`${CONFIG.API_BASE_URL}/group/users/${preferredUsername}/products`, { add: [product] })

      await AmplifyFetchAuthSession({ forceRefresh: true }); // Refresh the session to reflect the updated product access
      return { success: true };
    } catch (error) {
      console.error('Error enabling product for external sign-in:', error);

      return { success: false, error: 'label.loginError' };
    }
  }
}
